import type { Plan } from "@productlane/lib/src/browser"
import {
	PLAN_DISPLAY_NAMES,
	SLACK_WORKSPACE_LIMITS,
} from "@productlane/lib/src/browser"

/** Toast after Slack OAuth when the plan has no room for another workspace. */
export function slackWorkspaceLimitToast(plan: Plan): {
	title: string
	description: string
	canUpgrade: boolean
} {
	const limit = SLACK_WORKSPACE_LIMITS[plan]
	if (limit === 0) {
		return {
			title: "Your plan includes no Slack",
			description: "We didn't connect this one. Upgrade to Pro to connect it.",
			canUpgrade: true,
		}
	}
	if (limit < SLACK_WORKSPACE_LIMITS.scale) {
		return {
			title: `Your plan includes ${limit} Slack ${limit === 1 ? "workspace" : "workspaces"}`,
			description: `We didn't connect this one. Upgrade to Scale to connect up to ${SLACK_WORKSPACE_LIMITS.scale}.`,
			canUpgrade: true,
		}
	}
	return {
		title: `Your plan includes ${limit} Slack workspaces`,
		description:
			"We didn't connect this one. Disconnect a workspace to add it.",
		canUpgrade: false,
	}
}

/** Notice on the Slack card while the plan pauses some or all workspaces; null when nothing is paused. */
export function slackPausedNotice({
	plan,
	pausedCount,
	liveName,
}: {
	plan: Plan
	pausedCount: number
	liveName: string | null
}): string | null {
	if (pausedCount === 0) return null
	if (!liveName || SLACK_WORKSPACE_LIMITS[plan] === 0) {
		return pausedCount === 1
			? "Your plan includes no Slack, so your workspace is paused. Upgrade to Pro to sync again."
			: "Your plan includes no Slack, so your workspaces are paused. Upgrade to Pro to sync again."
	}
	const verb = pausedCount === 1 ? "is" : "are"
	const all = pausedCount === 1 ? "both" : "them all"
	return `Pro syncs one Slack workspace, so ${pausedCount} ${verb} paused. Upgrade to Scale to sync ${all}, or disconnect ${liveName} to sync the next one.`
}

/** Confirm before a plan change that pauses Slack workspaces. */
export function slackDowngradeConfirm(
	plan: Plan,
	pausedNames: string[],
): { title: string; description: string; confirmText: string } {
	const count = pausedNames.length
	const reason =
		SLACK_WORKSPACE_LIMITS[plan] === 0
			? `${PLAN_DISPLAY_NAMES[plan]} includes no Slack`
			: `${PLAN_DISPLAY_NAMES[plan]} syncs one Slack workspace`
	return {
		title: `Pause ${count} Slack ${count === 1 ? "workspace" : "workspaces"}?`,
		description: `${reason}, so ${count === 1 ? "this pauses" : "these pause"} until you upgrade again: ${pausedNames.join(", ")}.`,
		confirmText: `Downgrade to ${PLAN_DISPLAY_NAMES[plan]}`,
	}
}
